import { researchAIService } from './researchAIService';
import { userService } from './userService';
import type { WorkflowStatus } from '../components/WorkflowContext/WorkflowStatusColumn';
import type { Creator } from '../components/Creators/creatorTypes';
import type { TableColumn, CellData } from '../components/ResearchTable/types';

const API_BASE_URL = 'https://9vnb05of2j.execute-api.eu-north-1.amazonaws.com/development';

interface WorkflowSuggestion {
  suggestedStatus: WorkflowStatus;
  confidence: number; // 0-1
  reasoning: string;
  actionItems?: string[];
}

interface WorkflowAnalysisContext {
  creator: Creator;
  currentStatus: WorkflowStatus;
  tableData: Record<string, CellData>;
  columns: TableColumn[];
  projectContext?: string;
  tableGoal?: string;
  tableId?: string;
  projectId?: string;
}

class WorkflowAIService {
  /**
   * Analyze creator data and suggest next workflow status using dedicated Lambda
   */
  async suggestWorkflowStatus(context: WorkflowAnalysisContext): Promise<WorkflowSuggestion> {
    try {
      const { creator, currentStatus, tableData, columns, projectContext, tableGoal, tableId, projectId } = context;
      
      // Get auth token
      const token = await userService.getAuthToken();
      
      // Call the dedicated workflow status AI agent Lambda
      const response = await fetch(`${API_BASE_URL}/workflow-status-agent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          creator_id: creator.id,
          table_id: tableId,
          project_id: projectId,
          creator_info: {
            id: creator.id,
            name: creator.tiktok_data?.[0]?.handle || creator.instagram_data?.[0]?.handle || 'creator',
            tiktok_handle: creator.tiktok_data?.[0]?.handle,
            instagram_handle: creator.instagram_data?.[0]?.handle,
            followers: creator.tiktok_data?.[0]?.followers_count || creator.instagram_data?.[0]?.followers_count
          },
          table_data: tableData,
          table_config: {
            columns: columns.map(col => ({ id: col.id, name: col.name, cell_type: col.cell_type })),
            table_goal: tableGoal
          },
          current_status: currentStatus,
          project_context: projectContext
        })
      });

      if (!response.ok) {
        // Handle quota exhaustion
        if (response.status === 429) {
          console.warn('Workflow AI quota exhausted, using fallback');
          return this.getFallbackSuggestion(currentStatus);
        }
        
        throw new Error(`Workflow AI agent failed: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        return {
          suggestedStatus: result.suggested_status || currentStatus,
          confidence: Math.min(Math.max(result.confidence || 0.5, 0), 1),
          reasoning: result.reasoning || 'AI analysis completed',
          actionItems: result.action_items || []
        };
      }

      return this.getFallbackSuggestion(currentStatus);
    } catch (error) {
      console.error('Workflow AI suggestion failed:', error);
      return this.getFallbackSuggestion(context.currentStatus);
    }
  }

  /**
   * Build creator information summary
   */
  private buildCreatorInfo(creator: Creator): string {
    const platforms = [];
    
    if (creator.tiktok_data?.[0]) {
      const tiktok = creator.tiktok_data[0];
      platforms.push(`TikTok: @${tiktok.handle} (${tiktok.followers_count?.toLocaleString() || 'unknown'} followers)`);
    }
    
    if (creator.instagram_data?.[0]) {
      const instagram = creator.instagram_data[0];
      platforms.push(`Instagram: @${instagram.handle} (${instagram.followers_count?.toLocaleString() || 'unknown'} followers)`);
    }

    return `
Creator ID: ${creator.id}
Platforms: ${platforms.join(', ') || 'No platform data'}
Email: Not available in creator data
Contact Methods: ${this.getContactMethods(creator)}
`;
  }

  /**
   * Build data analysis from table cells
   */
  private buildDataAnalysis(tableData: Record<string, CellData>, columns: TableColumn[]): string {
    const analysis: string[] = [];
    
    columns.forEach(column => {
      const cellData = tableData[column.id];
      if (cellData?.value) {
        const status = cellData.loading ? 'Loading...' : 
                     cellData.error ? 'Error' : 
                     cellData.value ? 'Completed' : 'Empty';
        
        analysis.push(`${column.name}: ${status}`);
        
        if (column.cell_type === 'communication' && cellData.value) {
          analysis.push(`  - Communication data available`);
        }
        
        if (column.cell_type === 'outreach' && cellData.value) {
          analysis.push(`  - Outreach content: ${cellData.value.substring(0, 100)}...`);
        }
      }
    });

    return analysis.length > 0 ? analysis.join('\n') : 'No data available in table cells';
  }

  /**
   * Get available contact methods for creator
   */
  private getContactMethods(creator: Creator): string {
    const methods = [];
    
    if (creator.tiktok_data?.[0]) methods.push('TikTok DM');
    if (creator.instagram_data?.[0]) methods.push('Instagram DM');
    
    return methods.length > 0 ? methods.join(', ') : 'None available';
  }

  /**
   * Provide fallback suggestion when AI fails
   */
  private getFallbackSuggestion(currentStatus: WorkflowStatus): WorkflowSuggestion {
    const nextStatus = this.getLogicalNextStatus(currentStatus);
    
    return {
      suggestedStatus: nextStatus,
      confidence: 0.3,
      reasoning: 'Logical progression based on current status (AI analysis unavailable)',
      actionItems: this.getDefaultActionItems(nextStatus)
    };
  }

  /**
   * Get logical next status in workflow
   */
  private getLogicalNextStatus(currentStatus: WorkflowStatus): WorkflowStatus {
    const progression: Record<WorkflowStatus, WorkflowStatus> = {
      'not_started': 'in_progress',
      'in_progress': 'completed',
      'completed': 'completed',
      'skipped': 'skipped'
    };
    
    return progression[currentStatus] || currentStatus;
  }

  /**
   * Get default action items for status
   */
  private getDefaultActionItems(status: WorkflowStatus): string[] {
    const actionItems: Record<WorkflowStatus, string[]> = {
      'not_started': ['Send initial outreach', 'Research creator content style', 'Prepare campaign details'],
      'in_progress': ['Follow up on pending items', 'Track progress', 'Complete required actions'],
      'completed': ['Review deliverables', 'Process payment if applicable', 'Move to next stage'],
      'skipped': ['Document reason for skipping', 'Consider if stage is still needed']
    };
    
    return actionItems[status] || [];
  }

  /**
   * Batch analyze multiple creators for workflow suggestions
   */
  async batchSuggestWorkflowStatus(
    contexts: WorkflowAnalysisContext[]
  ): Promise<Record<string, WorkflowSuggestion>> {
    const suggestions: Record<string, WorkflowSuggestion> = {};
    
    // Process in batches to avoid overwhelming the API
    const batchSize = 3;
    for (let i = 0; i < contexts.length; i += batchSize) {
      const batch = contexts.slice(i, i + batchSize);
      const batchPromises = batch.map(async (context) => {
        const suggestion = await this.suggestWorkflowStatus(context);
        return { creatorId: context.creator.id, suggestion };
      });
      
      const batchResults = await Promise.all(batchPromises);
      batchResults.forEach(({ creatorId, suggestion }) => {
        suggestions[creatorId] = suggestion;
      });
      
      // Small delay between batches
      if (i + batchSize < contexts.length) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
    
    return suggestions;
  }
}

export const workflowAIService = new WorkflowAIService();
export type { WorkflowSuggestion, WorkflowAnalysisContext };
